<script lang="ts">
  // wiki/feature-offline-model-picker.md §"One-time offer".
  import { onMount } from 'svelte';
  import { beforeNavigate } from '$app/navigation';
  import { get } from 'svelte/store';
  import { loadModelCatalog } from '$lib/modelMirror/modelMirror';
  import { detectWebGpu, downloadSizeMb, getAvailableTiers, pickModel } from '$lib/utils/ai/inBrowserLlm';
  import {
    enableOfflineEngine,
    hydrateOfflineStoreFromStorage,
    offlineEngineStore,
    selectedOfflineModelId,
  } from '$lib/stores/offlineEngineStore';
  import { ConfirmDialog } from '$lib/ui';
  import { t } from '$lib/i18n';
  import { UiKeys } from '@voicedojo/backend-types';

  let { inTake }: { inTake: boolean } = $props();

  const SEEN_KEY = 'vd.offline.offer-seen';

  let sizeMb = $state<number | null>(null);
  let open = $state(false);

  onMount(() => {
    const whenIdle = window.requestIdleCallback ?? ((cb: () => void) => setTimeout(cb, 2000));
    whenIdle(() => void detect());
  });

  async function detect(): Promise<void> {
    try {
      // Hydrate first: "Yes" loads the saved model.
      if (localStorage.getItem(SEEN_KEY) || hydrateOfflineStoreFromStorage()) return;
      const detection = await detectWebGpu();
      if (!getAvailableTiers(detection).has('tiny')) return;
      const { models } = await loadModelCatalog();
      const model = pickModel(models, get(selectedOfflineModelId));
      sizeMb = Math.round(downloadSizeMb(model, detection.shaderF16));
    } catch {
      // Unflagged: a later GPU can still be offered.
    }
  }

  $effect(() => {
    if (sizeMb === null || open || inTake || $offlineEngineStore.status !== 'idle') return;
    try {
      localStorage.setItem(SEEN_KEY, '1');
    } catch {
      // Unflagged, it would return on every load.
      sizeMb = null;
      return;
    }
    open = true;
  });

  function close(): void {
    open = false;
    sizeMb = null;
  }
  // Never cover the page the privacy link opens.
  beforeNavigate(close);
</script>

{#if open}
  <ConfirmDialog
    title={t(UiKeys.Offline.OfferTitle)}
    body={t(UiKeys.Offline.OfferBody, { SizeMb: String(sizeMb) })}
    note={t(UiKeys.Offline.OfferPrivacy)}
    confirmLabel={t(UiKeys.Offline.OfferAccept)}
    confirmVariant="primary"
    cancelLabel={t(UiKeys.Offline.OfferDecline)}
    testid="offline-offer"
    onConfirm={() => {
      enableOfflineEngine();
      close();
    }}
    onCancel={close}
  />
{/if}
